import { Box, Divider } from "@mui/material";
import BookmarkPlan from "components/common/Bookmark";
import Export from "components/common/Export";
import { exportURLs } from "components/common/Export/ExportAPIEndpoints";
import { copyAvailableFiltersToPage } from "components/common/Filters/FiltersFunctions";
import { filterPage } from "components/common/Filters/FiltersHelper";
import GlobalFilterDescription from "components/common/Filters/GlobalFilterDescription";
import GlobalFilters from "components/common/Filters/GlobalFilters";
import KeyMetrics from "components/common/KeyMetrics/KeyMetrics";
import BasicTable from "components/common/Table";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { PAGE_SIZE, abovePropertyViewTypeTemplate, noAbovePropertyPlans, tabConstants } from "utils/CommonConstants";
import {
  formFilterRequestPayload,
  functionalAreaIdToName,
  functionalAreaIdsToNames,
  getHltInncodes,
  getMoreValues,
  isAboveUserPlanBookmarked
} from "utils/CommonFunctions";
import { routes } from "utils/routes";
import { serviceTypes } from "utils/Types";
import { fetchAssignedLibraryPlansAsync, setTemplatePlansSnapshot } from "../../AboveProperty.slice";
import { getKeyMetricsTemplatePlanViewData } from "../../AbovePropertyHelper";
import { headers } from "./TableHeadersConfig";

interface Props {}

const AssignedCustomPlans = (props: Props) => {
  const navigate = useNavigate();

  const [pageNo, setPageNo] = useState(0);
  const dispatch: any = useAppDispatch();
  const functionalAreaList: any = useAppSelector((state) => state.functionalAreas.list);
  const totalRecords = useAppSelector((state) => state.aboveProperty.totalRecords);
  const assignedTemplatePlan = useAppSelector((state) => state.aboveProperty.assignedLibraryPlan);
  // TODO : This is a required dependency to update component when bookmarked plans are changed.
  const bookmarkedTemplate = useAppSelector((state) => state.aboveProperty.bookmarkedTemplate);
  const templatePlansSnapshot = useAppSelector((state) => state.aboveProperty.templatePlansSnapshot);
  const pageName = filterPage.plansPageTemplatePlansDisplay;
  const globalFiltersforPage = useAppSelector((state) => state.globalFilters[pageName]);
  const filterRequestPayload = globalFiltersforPage?.modifiedFilter || globalFiltersforPage?.defaultFilter || [];
  const formattedFilterRequestPayload = formFilterRequestPayload(filterRequestPayload);
  const searchText = globalFiltersforPage.searchText;

  useEffect(() => {
    dispatch(
      fetchAssignedLibraryPlansAsync({
        view: "template_plan",
        innCodes: getHltInncodes(),
        ...formattedFilterRequestPayload,
        searchText: searchText,
        pageable: {
          page: pageNo,
          size: PAGE_SIZE
        }
      })
    );
  }, [pageNo, filterRequestPayload, searchText]);

  useEffect(() => {
    setPageNo(0);
  }, [filterRequestPayload, searchText]);

  useEffect(() => {
    dispatch(
      fetchAssignedLibraryPlansAsync({
        view: "template_plan",
        innCodes: getHltInncodes(),
        ...formattedFilterRequestPayload,
        searchText: searchText,
        fetchSnapshot: true
      })
    ).then((res: any) => {
      dispatch(setTemplatePlansSnapshot(res?.payload));
    });
  }, [filterRequestPayload, searchText]);

  return (
    <Box sx={{ backgroundColor: tokens.Background.General.Primary, borderRadius: "12px", overflow: "hidden" }}>
      <Box sx={{ padding: "16px 8px" }}>
        {" "}
        <GlobalFilters isSearchBarVisible page={pageName} isPreDefinedNav={true} searchedText={searchText} />
        <GlobalFilterDescription page={pageName} />
        <Box sx={{ marginTop: "16px" }}>
          <KeyMetrics
            keyMetricsData={getKeyMetricsTemplatePlanViewData(templatePlansSnapshot)}
            helperText="*Last 90 Days"
          />
        </Box>
      </Box>
      {assignedTemplatePlan?.length ? (
        <Box sx={{ display: "flex", marginLeft: "auto", justifyContent: "flex-end", margin: "-8px 9px 16px 0px" }}>
          <Export
            URL={exportURLs.aboveProperty_assignedAndTemplatePlans}
            data={{
              view: "template_plan",
              innCodes: getHltInncodes(),
              ...formattedFilterRequestPayload,
              searchText: searchText,
              pageable: {
                page: 0,
                size: 50
              }
            }}
            serviceType={serviceTypes.userplan}
          />
        </Box>
      ) : (
        <></>
      )}
      <Divider />
      <BasicTable
        data={{
          headers,
          rows: assignedTemplatePlan?.map((plan: any) => {
            return {
              data: [
                {
                  content: (
                    <Box
                      onClick={() => {
                        copyAvailableFiltersToPage(
                          dispatch,
                          filterPage.planProgressFromPlansDisplay,
                          filterRequestPayload
                        );
                        navigate(
                          `${routes.PlanProgress}/${plan?.templatePlanId}?redirectFrom=${tabConstants.AssignedCustomPlans}`,
                          {
                            state: { progressInncodes: plan?.properties }
                          }
                        );
                      }}
                      sx={{
                        cursor: "pointer",
                        textDecoration: "none",
                        color: tokens.Text.General.PrimaryHeadline,
                        fontWeight: "700"
                      }}
                    >
                      {plan?.name}
                    </Box>
                  ),
                  showBold: true
                },
                {
                  content: getMoreValues(functionalAreaIdsToNames(plan.functionalAreaIds)),
                  tooltip: {
                    title: "Functional Areas",
                    list: plan.functionalAreaIds?.map((FA: any) => {
                      return { name: functionalAreaIdToName(FA, functionalAreaList) };
                    })
                  }
                },

                { content: plan?.properties?.length },
                {
                  content: (
                    <BookmarkPlan
                      aboveUserPayload={{
                        isAboveUsertype: true,
                        viewType: abovePropertyViewTypeTemplate
                      }}
                      cmsId={plan?.templatePlanId}
                      isBookmarked={isAboveUserPlanBookmarked(plan?.templatePlanId, "bookmarkedTemplate")}
                    />
                  )
                }
              ]
            };
          })
        }}
        totalRecords={totalRecords}
        noDataMessage={noAbovePropertyPlans}
        pagination={{ pageNo, setPageNo }}
      />
    </Box>
  );
};

export default AssignedCustomPlans;
